import { RefObject, useEffect, useState } from 'react'

/**
 * True once `ref`'s bottom edge has scrolled above `offset` px from the top.
 *
 * Written for the certificate page's scroll-linked header: the hero photograph
 * is the element, the header's own height is the offset, so the flag flips at
 * exactly the moment the image stops being behind the controls.
 *
 * ## Measuring the ELEMENT, not a scroll position
 *
 * The obvious version — `window.scrollY > 375` — bakes in the hero's height. The
 * hero is an image whose height depends on the product's photograph, and it
 * collapses to a placeholder when there is none, so a constant would be wrong
 * for most certificates and very wrong for the no-image case. Reading the
 * element's own rect asks the question directly and needs no per-page tuning.
 *
 * ## Why it also runs on mount
 *
 * A page can start already scrolled: the certificate page restores its position
 * when you come back from a sub-view. Deriving the flag only from scroll EVENTS
 * would leave the header transparent over content until the user happened to
 * scroll, so the first read happens on mount.
 *
 * That same first read is what makes this degrade sensibly where scroll events
 * do not fire at all (the app's preview pane reports `visibilityState: hidden`):
 * the header renders in the correct state for the current position, it simply
 * does not update. A missing transition, not a broken header.
 */
export const useScrolledPast = (
  ref: RefObject<HTMLElement | null>,
  offset = 0,
): boolean => {
  const [past, setPast] = useState(false)

  useEffect(() => {
    const read = () => {
      const el = ref.current
      // No element to measure — treat as "past", so a page with no hero gets the
      // solid header rather than transparent controls over its first content.
      if (!el) {
        setPast(true)
        return
      }
      setPast(el.getBoundingClientRect().bottom <= offset)
    }

    read()
    // `passive`: this listener never calls preventDefault, and saying so keeps it
    // off the browser's critical path for scrolling.
    window.addEventListener('scroll', read, { passive: true })
    window.addEventListener('resize', read)
    return () => {
      window.removeEventListener('scroll', read)
      window.removeEventListener('resize', read)
    }
  }, [ref, offset])

  return past
}
